import React, { useEffect, useMemo, useState } from 'react';
import { Image, Linking, Platform, Pressable, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppTheme, type ThemeColors } from '@/theme';
import { ScaledSheet } from '@/theme/responsive';
import { trText } from '@/i18n/plain';
import { detectWebDevice, isInAppBrowser, isInstalledApp, openInAppUrl, useInstallApp, type WebDevice } from '@/utils/pwa';
import { useLanguage } from '@/i18n';

const STORAGE_KEY = 'salunbd.installPrompt.dismissedAt';
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

function snoozed(): boolean {
  try {
    const at = Number(window.localStorage.getItem(STORAGE_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < SNOOZE_MS;
  } catch {
    return false;
  }
}

/**
 * Shown once a week to people who open the web app on a phone: Android visitors get an Open in the app
 * button (the Play Store if they do not have it), iPhone visitors are shown how to install the web app to their home screen. It never
 * appears on a computer (there the side rail has an Install button), inside the installed app, or after "Continue on web".
 */
export function InstallPrompt() {
  useLanguage(); // redraw when the language changes (trText is read at call time)
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const insets = useSafeAreaInsets();
  const [device, setDevice] = useState<WebDevice>('other');
  const [open, setOpen] = useState(false);
  const { canInstall, install } = useInstallApp();

  // Decided after mount: the static export runs on a server with no browser to inspect.
  useEffect(() => {
    if (Platform.OS !== 'web' || isInstalledApp() || snoozed()) return;
    const found = detectWebDevice();
    if (found === 'other') return;
    setDevice(found);
    setOpen(true);
  }, []);

  if (!open) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Private mode: it simply shows again next visit.
    }
    setOpen(false);
  };

  const steps: { icon: keyof typeof Ionicons.glyphMap; text: string }[] = [
    {
      icon: 'share-outline',
      text: isInAppBrowser()
        ? trText('Open this page in Safari first, then tap Share', 'আগে Safari-তে পেজটি খুলুন, তারপর Share ট্যাপ করুন')
        : trText('Tap the Share button in your browser', 'ব্রাউজারের Share বাটনে ট্যাপ করুন'),
    },
    { icon: 'add-circle-outline', text: trText('Choose Add to Home Screen', 'Add to Home Screen বেছে নিন') },
    {
      icon: 'checkmark-circle-outline',
      text: trText('Tap Add. SalunBD opens like any other app', 'Add ট্যাপ করুন। SalunBD অন্য যেকোনো অ্যাপের মতো খুলবে'),
    },
  ];

  return (
    <View style={styles.overlay} accessibilityViewIsModal accessibilityLabel={trText('Get the SalunBD app', 'SalunBD অ্যাপ নিন')}>
      <Pressable style={styles.backdrop} onPress={dismiss} accessibilityRole="button" accessibilityLabel={trText('Close', 'বন্ধ করুন')} />
      <View style={[styles.sheet, { paddingBottom: Math.max(insets.bottom, 16) + 8 }]} accessibilityRole="alert">
        <View style={styles.headerRow}>
          <Image source={require('@/assets/images/icon.png')} style={styles.appIcon} />
          <View style={styles.headerText}>
            <Text style={styles.title}>
              {device === 'android'
                ? trText('Open SalunBD in the app', 'SalunBD অ্যাপে খুলুন')
                : trText('Install SalunBD on your iPhone', 'আপনার iPhone-এ SalunBD ইনস্টল করুন')}
            </Text>
            <Text style={styles.subtitle}>
              {device === 'android'
                ? trText('Opens the app if you have it, or takes you to Google Play. Free.', 'অ্যাপ থাকলে অ্যাপ খুলবে, না থাকলে Google Play-তে নিয়ে যাবে। ফ্রি।')
                : trText('No App Store needed. It takes three taps.', 'App Store লাগবে না। মাত্র তিনটি ট্যাপ।')}
            </Text>
          </View>
        </View>

        {device === 'ios' && (
          <View style={styles.steps}>
            {steps.map((step, index) => (
              <View key={step.icon} style={styles.stepRow}>
                <View style={styles.stepIcon}>
                  <Ionicons name={step.icon} size={20} color={colors.accent} />
                </View>
                <Text style={styles.stepText}>
                  {index + 1}. {step.text}
                </Text>
              </View>
            ))}
          </View>
        )}

        {device === 'android' && (
          <Pressable style={styles.primary} onPress={() => void Linking.openURL(openInAppUrl())} accessibilityRole="link">
            <Ionicons name="logo-google-playstore" size={20} color={colors.white} />
            <Text style={styles.primaryText}>{trText('Open in the app', 'অ্যাপে খুলুন')}</Text>
          </Pressable>
        )}

        {device === 'android' && canInstall && (
          <Pressable
            style={styles.outline}
            onPress={() => {
              void install();
              dismiss();
            }}
            accessibilityRole="button">
            <Ionicons name="download-outline" size={20} color={colors.accent} />
            <Text style={styles.outlineText}>{trText('Install the web app instead', 'এর বদলে ওয়েব অ্যাপ ইনস্টল করুন')}</Text>
          </Pressable>
        )}

        <Pressable style={styles.secondary} onPress={dismiss} accessibilityRole="button">
          <Text style={styles.secondaryText}>{trText('Continue on web', 'ওয়েবেই চালিয়ে যান')}</Text>
        </Pressable>
      </View>
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  ScaledSheet.create({
    overlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, justifyContent: 'flex-end' },
    backdrop: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(8, 11, 20, 0.55)' },
    sheet: {
      width: '100%',
      maxWidth: 480,
      alignSelf: 'center',
      backgroundColor: colors.surface,
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
      paddingTop: 20,
      paddingHorizontal: 20,
      gap: 16,
      borderWidth: 1,
      borderBottomWidth: 0,
      borderColor: colors.border,
    },
    headerRow: { flexDirection: 'row', alignItems: 'center', gap: 14 },
    appIcon: { width: 56, height: 56, borderRadius: 14 },
    headerText: { flex: 1, gap: 2 },
    title: { fontSize: 18, lineHeight: 24, fontFamily: 'Manrope_800ExtraBold', color: colors.textPrimary },
    subtitle: { fontSize: 14, lineHeight: 20, color: colors.textSecondary },
    steps: { gap: 10 },
    stepRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
    stepIcon: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.accentSoft },
    stepText: { flex: 1, fontSize: 14, lineHeight: 20, color: colors.textPrimary },
    primary: {
      minHeight: 52,
      borderRadius: 14,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      backgroundColor: colors.accentFill,
    },
    primaryText: { fontSize: 16, fontFamily: 'Manrope_800ExtraBold', color: colors.white },
    outline: { minHeight: 48, borderRadius: 14, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, borderWidth: 1.5, borderColor: colors.accent },
    outlineText: { fontSize: 15, fontFamily: 'Manrope_700Bold', color: colors.accent },
    secondary: { minHeight: 44, alignItems: 'center', justifyContent: 'center' },
    secondaryText: { fontSize: 15, fontFamily: 'Manrope_700Bold', color: colors.textSecondary },
  });
